import { Expression, Statement, LoweredNode } from './ExpressionIR';
import { collectReferencedVars } from './LoweredEmitter';

interface StackFrameInfo {
  stackPointerName: string;
  frameVarName: string;
  // The local that captured `global_get(__stack_pointer)`, when the prologue
  // saves it before subtracting (Pattern 1). Null for the direct form.
  savedStackPointerVar: string | null;
  frameSize: number;
}

function isStackPointerGlobal(name: string): boolean {
  return name === '__stack_pointer' || name === 'sp' || name === 'stack_pointer';
}

function isGlobalSetToStackPointer(statement: Statement): { name: string; value: Expression } | null {
  if (statement.kind === 'global_set' && isStackPointerGlobal(statement.name)) {
    return { name: statement.name, value: statement.value };
  }
  return null;
}

/**
 * A frame-size constant can be mis-resolved to a string literal when its numeric
 * value happens to fall inside a data segment (the address resolver is
 * context-free). Recover the numeric value from either form.
 */
function frameSizeConstant(expression: Expression): number | null {
  if (expression.kind === 'const') {
    return Number(expression.value);
  }
  if (expression.kind === 'string_literal') {
    return expression.address;
  }
  return null;
}

/** Returns the frame size N if `expression` is `<stackPointer> - N`, else null. */
function frameAllocSize(expression: Expression, savedStackPointerVar: string | null): number | null {
  if (expression.kind !== 'binary' || expression.op !== '-') {
    return null;
  }
  const base = expression.left;
  const baseIsStackPointer =
    (base.kind === 'global' && isStackPointerGlobal(base.name)) ||
    (base.kind === 'var' && savedStackPointerVar !== null && base.name === savedStackPointerVar);
  if (!baseIsStackPointer) {
    return null;
  }
  return frameSizeConstant(expression.right);
}

function extractFrameAlloc(statements: Statement[]): StackFrameInfo | null {
  const prologueWindow = Math.min(statements.length, 5);
  for (let statementIndex = 0; statementIndex < prologueWindow; statementIndex++) {
    const statement = statements[statementIndex];
    if (statement.kind !== 'assign') {
      continue;
    }

    // Pattern 1: sp = global_get(__stack_pointer); ...; fp = sp - N
    if (statement.value.kind === 'global' && isStackPointerGlobal(statement.value.name)) {
      const savedStackPointerVar = statement.target;
      const stackPointerName = statement.value.name;
      const allocLimit = Math.min(statements.length, statementIndex + 4);
      for (let allocIndex = statementIndex + 1; allocIndex < allocLimit; allocIndex++) {
        const allocStatement = statements[allocIndex];
        if (allocStatement.kind !== 'assign') {
          continue;
        }
        const frameSize = frameAllocSize(allocStatement.value, savedStackPointerVar);
        if (frameSize !== null) {
          return { stackPointerName, frameVarName: allocStatement.target, savedStackPointerVar, frameSize };
        }
      }
    }

    // Pattern 2: fp = __stack_pointer - N (direct)
    if (statement.value.kind === 'binary' && statement.value.left.kind === 'global' &&
        isStackPointerGlobal(statement.value.left.name)) {
      const frameSize = frameAllocSize(statement.value, null);
      if (frameSize !== null) {
        return { stackPointerName: statement.value.left.name, frameVarName: statement.target, savedStackPointerVar: null, frameSize };
      }
    }
  }

  return null;
}

function isEpilogueRestore(statement: Statement, frameInfo: StackFrameInfo): boolean {
  const globalSet = isGlobalSetToStackPointer(statement);
  if (!globalSet) {
    return false;
  }

  // __stack_pointer = fp + N (frame size matching the prologue allocation)
  if (globalSet.value.kind === 'binary' && globalSet.value.op === '+' &&
      globalSet.value.left.kind === 'var' && globalSet.value.left.name === frameInfo.frameVarName) {
    const restoreSize = frameSizeConstant(globalSet.value.right);
    if (restoreSize !== null && restoreSize === frameInfo.frameSize) {
      return true;
    }
  }

  // __stack_pointer = sp (restoring the value saved by the prologue)
  if (globalSet.value.kind === 'var' && frameInfo.savedStackPointerVar !== null &&
      globalSet.value.name === frameInfo.savedStackPointerVar) {
    return true;
  }

  return false;
}

function isStackFramePrologue(statement: Statement, frameInfo: StackFrameInfo): boolean {
  // sp = global_get(__stack_pointer) — the prologue read of the stack pointer
  if (statement.kind === 'assign' && statement.value.kind === 'global' &&
      isStackPointerGlobal(statement.value.name)) {
    return true;
  }

  // fp = sp - N — only when the value is the actual frame allocation, not any
  // later reuse of the same variable.
  if (statement.kind === 'assign' && statement.target === frameInfo.frameVarName &&
      frameAllocSize(statement.value, frameInfo.savedStackPointerVar) !== null) {
    return true;
  }

  // __stack_pointer = fp (committing the frame pointer)
  if (statement.kind === 'global_set' && isStackPointerGlobal(statement.name) &&
      statement.value.kind === 'var' && statement.value.name === frameInfo.frameVarName) {
    return true;
  }

  return false;
}

function filterStatements(statements: Statement[], frameInfo: StackFrameInfo): Statement[] {
  return statements.filter(statement => {
    if (isStackFramePrologue(statement, frameInfo)) {
      return false;
    }
    if (isEpilogueRestore(statement, frameInfo)) {
      return false;
    }
    return true;
  });
}

function processNode(node: LoweredNode, frameInfo: StackFrameInfo): LoweredNode {
  switch (node.kind) {
    case 'block':
      return { kind: 'block', body: filterStatements(node.body, frameInfo) };
    case 'sequence':
      return { kind: 'sequence', children: node.children.map(child => processNode(child, frameInfo)) };
    case 'if':
      return {
        kind: 'if',
        condition: node.condition,
        thenBody: processNode(node.thenBody, frameInfo),
        elseBody: node.elseBody ? processNode(node.elseBody, frameInfo) : null,
      };
    case 'while':
      return { kind: 'while', condition: node.condition, body: processNode(node.body, frameInfo) };
    case 'do_while':
      return { kind: 'do_while', body: processNode(node.body, frameInfo), condition: node.condition };
    case 'for':
      return { kind: 'for', init: node.init, condition: node.condition, increment: node.increment, body: processNode(node.body, frameInfo) };
    case 'labeled_block':
      return { kind: 'labeled_block', label: node.label, body: processNode(node.body, frameInfo) };
    case 'switch':
      return {
        kind: 'switch',
        selector: node.selector,
        cases: node.cases.map(caseEntry => ({ values: caseEntry.values, body: processNode(caseEntry.body, frameInfo) })),
        defaultBody: processNode(node.defaultBody, frameInfo),
      };
    default:
      return node;
  }
}

function removeBareSPReferences(node: LoweredNode): LoweredNode {
  const referencedNames = new Set<string>();
  collectReferencedVars(node, referencedNames);
  return stripBareStackPointer(node, referencedNames);
}

function stripBareStackPointer(node: LoweredNode, referencedNames: Set<string>): LoweredNode {
  switch (node.kind) {
    case 'block': {
      const filtered = node.body.filter(statement => {
        if (statement.kind === 'global_set' && isStackPointerGlobal(statement.name)) {
          return false;
        }
        // Drop a stack-pointer read only when its result is never used.
        if (statement.kind === 'assign' && statement.value.kind === 'global' &&
            isStackPointerGlobal(statement.value.name) && !referencedNames.has(statement.target)) {
          return false;
        }
        return true;
      });
      return { kind: 'block', body: filtered };
    }
    case 'sequence':
      return { kind: 'sequence', children: node.children.map(child => stripBareStackPointer(child, referencedNames)) };
    case 'if':
      return { kind: 'if', condition: node.condition, thenBody: stripBareStackPointer(node.thenBody, referencedNames), elseBody: node.elseBody ? stripBareStackPointer(node.elseBody, referencedNames) : null };
    case 'while':
      return { kind: 'while', condition: node.condition, body: stripBareStackPointer(node.body, referencedNames) };
    case 'do_while':
      return { kind: 'do_while', body: stripBareStackPointer(node.body, referencedNames), condition: node.condition };
    case 'for':
      return { kind: 'for', init: node.init, condition: node.condition, increment: node.increment, body: stripBareStackPointer(node.body, referencedNames) };
    case 'labeled_block':
      return { kind: 'labeled_block', label: node.label, body: stripBareStackPointer(node.body, referencedNames) };
    case 'switch':
      return { kind: 'switch', selector: node.selector, cases: node.cases.map(caseEntry => ({ values: caseEntry.values, body: stripBareStackPointer(caseEntry.body, referencedNames) })), defaultBody: stripBareStackPointer(node.defaultBody, referencedNames) };
    default:
      return node;
  }
}

function getFirstBlock(node: LoweredNode): Statement[] | null {
  if (node.kind === 'block') {
    return node.body;
  }
  if (node.kind === 'sequence' && node.children.length > 0) {
    return getFirstBlock(node.children[0]);
  }
  return null;
}

export interface StackFrameResult {
  node: LoweredNode;
  frameVarName: string | null;
  frameSize: number;
}

export function removeStackFrame(node: LoweredNode): StackFrameResult {
  const firstBlock = getFirstBlock(node);
  if (!firstBlock) {
    return { node, frameVarName: null, frameSize: 0 };
  }

  const frameInfo = extractFrameAlloc(firstBlock);
  if (!frameInfo) {
    // Even without a frame allocation, remove bare __stack_pointer global_set/global_get
    return { node: removeBareSPReferences(node), frameVarName: null, frameSize: 0 };
  }

  return {
    node: processNode(node, frameInfo),
    frameVarName: frameInfo.frameVarName,
    frameSize: frameInfo.frameSize,
  };
}
